import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../templates/DefaultLayout';
import { Redirect } from 'react-router-dom';
import { useForm, useAuth, useRouter } from '~/hooks';
import { getRolesByTitle, getRoleNameByRoleId, ROLES_ID_MAP, ROLES, TITLES } from '~/util/auth';
import FormGroup from '~/components/form/form-group';
import FormSelect from '~/components/form/form-select';
import FormRadio from '~/components/form/form-radio';
import { getFirms } from '~/api/lawfirm';
import { get, isEmpty } from 'lodash';
import { auth } from '~/api';
import { auth as authUtil, stringUtils } from '~/util';
import { getUserDetails } from '~/api/auth';
import {  resetPasswordRequest  } from '~/api';
import DeleteUser from  './DeleteUser';
import { getOrderContactsByFirm } from '~/api/orderContacts';

const parseFirm = input => {
    const { Name, Address1, Address2, City, State, Id} = input;
    let address = '';
    if(Address1) address = Address1;
    if(Address2) address = `${address} ${Address2}`;
    return {
        id: Id,
        name: Name,
        address,
        city: City,
        state: State
    };
};

const initialState = {
    firstName: '',
    lastName: '',
    lawFirm: {},
    contact: null,
    title: {},
    role: {},
    roles: [],
    address: '',
    city: '',
    state: '',
    active: 'true',
    sendPPTXFile: 'true',
    emailAddress: '',
    userName: '',
    password:'',
    confirmPassword:'',
    initialUsername: '',
    showDeleteModal: false,
    firms: [],
    contactOptions: [],
    redirect: false,
    showFirmInfo: false,
    userId: null
};

const loadData = async (userId, values) => {
    const firmsData = await getFirms();
    const userDetails = await getUserDetails(userId);

    let firms = Array.isArray(firmsData)
        ? firmsData.map(parseFirm)
        : [];

    const { firstName, lastName, emailAddress, username, roleId, firmId, contactId, isActive } = userDetails;

    const initialUsername = username;
    
    const preferences = JSON.parse(userDetails.preferences);
    const roleName = getRoleNameByRoleId(roleId);
    const selectedFirm = firmId 
        ? firmsData.find(firm => firm.Id == firmId)
        : null;

    let contactOptions = [];
    let contact = null;

    if (stringUtils.hasValue(firmId)) {
        const contacts = await getOrderContactsByFirm(firmId);
        contactOptions = contacts;

        if (stringUtils.hasValue(contactId)) {
            let fullContact = contacts.find(c => c.Id == contactId);
            if (fullContact != null) {
                contact = {
                    label: `${fullContact.FirstName} ${fullContact.LastName}`,
                    value: contactId
                };
            }
        }
    }

    const sendPXT = preferences?.sendPPTXFile || false;

    const formData = {
        ...values,
        userId,
        firstName,
        lastName,
        emailAddress,
        userName: username,
        initialUsername,
        firms,
        contact,
        contactOptions,      
        role: {
            label: roleName,
            value: roleName
        },
        showFirmInfo: !authUtil.isTryon(roleId),
        active: `${isActive}`,
        sendPPTXFile: `${sendPXT}`
    };

    if (selectedFirm) {
        formData.address = selectedFirm.Address1;
        formData.stateName = selectedFirm.State;
        formData.city = selectedFirm.City;
        formData.lawFirm = {
            label: selectedFirm.Name,
            value: firmId
        };
    }

    if (preferences) {
        formData.title = {
            label: preferences.title,
            value: preferences.title
        };

        formData.roles = getRolesByTitle(preferences.title).map(role => {
            return getRoleNameByRoleId(role);
        });
    }
    
    return formData;
};

const onSubmit = async ({ values, onFail, onInvalidForm, resetFormErrors }) => {
    try {
        resetFormErrors();
        let errors = {};
        if (!values.userName) errors.userName = 'Username is required';
        if (values.userName.length < 5) errors.userName = 'Username must have a minimum of 5 characters';
        if (!values.title) errors.title = 'Title is required';
        if (!values.lawFirm) errors.lawFirm = 'Law Firm is required';
        if (!values.emailAddress) errors.emailAddress = 'Email address is required';
        if (!isEmpty(errors)) return onInvalidForm(errors);
        
        const payload = {
            firstName: values.firstName,
            emailAddress: values.emailAddress,
            lastName: values.lastName,
            lawFirm: values.lawFirm.label,
            firmId:  values.lawFirm.value,
            title: values.title,
            active: values.active,
            userName: values.initialUsername,
            sendPPTXFile: values.sendPPTXFile,
            roleId: ROLES_ID_MAP[values.role.value]
        };

        if (values.contact?.value) {
            payload.contactId = values.contact?.value;
        }
        
        if(values.initialUsername !== values.userName){
            payload.newUserName = values.userName;
        }
        if(!isEmpty(values.address) && !isEmpty(values.city) && !isEmpty(values.stateName)){
            payload.lawfirmAddress = values.address + ', ' + values.city + ', ' + values.stateName;
        }
        const response = await auth.updateUserDetails(values.userId, payload);
        if(!response || response.errored){
            errors.formError = response.error || 'Failed to add user';
            return onInvalidForm(errors);
        }

    } catch (e) {
        return onFail(e.message);
    }
};

const EditUser = () => {
    const { params, history} = useRouter();
    const { userId } = params;

    const authCtx = useAuth();
    
    const onResetPassword = async () => {
        try{
            await resetPasswordRequest.sendResetPasswordLink(userId);
            history.push('/useradmin');
        }catch(e){
            formActions.onFail(e.message); 
        }
    };

    const onDeleteUser = async() => {
        fieldActions.onChange(true, 'showDeleteModal');
    };

    const hideDeleteModal = async() => {
        fieldActions.onChange(false, 'showDeleteModal');
    };

    const onLoad = useCallback(async (values) => {
        const data = await loadData(userId, values);
        const userFirm = get(data, 'lawFirm.value', null);

        const redirect = !authCtx.isWithFirm(userFirm) && !authCtx.isSiteAdmin;

        return {
            ...data,
            redirect,
            showFirmInfo: authCtx.isSiteAdmin
        };
    }, [userId, authCtx.token]);

    const {
        state,
        formActions,
        fieldActions
    } = useForm({
        initialState,
        onSubmit,
        onLoad
    });

    if (state.values.redirect) {
        return <Redirect to='/unauthorized' push />;
    }

    return (
        <Layout title="Edit User" subtitle="">
            <div className="container flex items-stretch justify-between flex-col py-4">
                <div className="grid grid-cols-4 grid-flow-row gap-y-8 gap-x-4">
                    <FormGroup
                        label="First Name"
                        groupClassName="col-span-2" 
                        labelClassName="text-sm font-medium" 
                        placeholder="First Name"
                        path="firstName"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.firstName}
                        error={state.errors.firstName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Last Name"
                        labelClassName="text-sm font-medium"
                        groupClassName="col-span-2"  
                        placeholder="Last Name"
                        path="lastName"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.lastName}
                        error={state.errors.lastName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Email Address"
                        labelClassName="text-sm font-medium"   
                        placeholder="Email Address"
                        path="emailAddress"
                        groupClassName="col-span-2"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.emailAddress}
                        error={state.errors.emailAddress}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Username"
                        labelClassName="text-sm font-medium"  
                        placeholder="Username"
                        path="userName"
                        groupClassName="col-span-2"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.userName}
                        error={state.errors.userName}
                        {...fieldActions}
                    />
                    <FormSelect
                        label="Title"
                        groupClassName="col-span-2 text-sm font-medium" 
                        required
                        options={authCtx.loadAccessibleTitles()}
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='title'
                        placeholder="Please select a title"
                        value={state.values.title}
                        error={state.errors.title}
                        {...fieldActions}
                        onChange={(selectedTitle) => {
                            let roles = getRolesByTitle(selectedTitle.value);
                            roles = roles.map(role => {
                                return getRoleNameByRoleId(role);
                            });
                            fieldActions.onChange(roles, 'roles');
                            fieldActions.onChange({
                                label: roles[0],
                                value: roles[0]
                            }, 'role');

                            if (selectedTitle.value == TITLES.TRYON_EMPLOYEE) {
                                fieldActions.onChange(null, 'lawFirm');
                                fieldActions.onChange('', 'address');
                                fieldActions.onChange('', 'city');
                                fieldActions.onChange('', 'stateName');
                                fieldActions.onChange(null, 'contact');
                            }

                            fieldActions.onChange(selectedTitle, 'title');
                        }}
                    />
                    <FormSelect
                        required
                        label="Role"
                        groupClassName="col-span-2 text-sm font-medium" 
                        options={state.values.roles.map(role=>{
                            return{
                                label: role,
                                value: role
                            };
                        })}
                        placeholder="Please select a role"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='role'
                        value={state.values.role}
                        error={state.errors.role}
                        {...fieldActions}
                    />
                    {state.values.showFirmInfo &&
                        <>
                            <FormSelect
                                label="Law Firm"
                                isSearchable
                                groupClassName="col-span-2 text-sm font-medium" 
                                required
                                options={state.values.firms.map(firm => {
                                    return {
                                        label: firm.name,
                                        value: firm.id
                                    };
                                })}
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                path='lawFirm'
                                value={state.values.lawFirm}
                                error={state.errors.lawFirm}
                                {...fieldActions}
                                onChange={(selectedFirm) => {
                                    const firm = state.values.firms.find(f=>f.id === selectedFirm.value);
                                    fieldActions.onChange(firm.address, 'address');
                                    fieldActions.onChange(selectedFirm, 'lawFirm');
                                    fieldActions.onChange(firm.city, 'city');
                                    fieldActions.onChange(firm.state, 'stateName');

                                    return getOrderContactsByFirm(selectedFirm.value)
                                        .then(c => fieldActions.onChange(c, 'contactOptions'));
                                }}
                            />
                            {stringUtils.hasValue(state.values.lawFirm) && authCtx.user.roleId == ROLES.ADMIN && (
                                <FormSelect 
                                    label="SoftPro Contact"
                                    isSearchable
                                    isClearable
                                    groupClassName="col-span-2 text-sm font-medium"
                                    options={state.values.contactOptions.map(o => {
                                        return {
                                            label: `${o.FirstName} ${o.LastName}`,
                                            value: o.Id
                                        };
                                    })}
                                    inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                                    path='contact'
                                    value={state.values.contact}
                                    error={state.errors.contact}
                                    {...fieldActions}
                                />
                            )}
                            <FormGroup
                                label="Address"
                                labelClassName="text-sm font-medium" 
                                readOnly 
                                path="address"
                                groupClassName="col-span-2 col-start-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.address}
                            />
                            <FormGroup
                                label="City"
                                labelClassName="text-sm font-medium"  
                                path="city"
                                readOnly
                                groupClassName="col-span-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.city}
                            />
                            <FormGroup
                                label="State"
                                labelClassName="text-sm font-medium" 
                                readOnly
                                path="state"
                                groupClassName="col-span-1"
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={state.values.stateName}
                            />
                        </>
                    }
                </div>
                {state.values.showFirmInfo &&
                    <div className="mt-4">
                        <label className="text-sm font-medium mr-8">Send PXT File?</label>
                        <FormRadio
                            label="Send PPTX File?" 
                            path="sendPPTXFile"
                            groupClassName="col-span-2"
                            inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                            value={state.values.sendPPTXFile}
                            error={state.errors.sendPPTXFile}
                            {...fieldActions}
                            options={[{value: 'true', key:'Yes'},{value: 'false', key: 'No'}]}
                            onChange={(val)=>{
                                fieldActions.onChange(val, 'sendPPTXFile');
                            }}
                        />
                    </div>
                }
                <div className="mt-4">
                    <label className="text-sm font-medium mr-8">Active?</label>
                    <FormRadio
                        name="Active" 
                        path="active"
                        groupClassName="col-span-2"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.active}
                        error={state.errors.active}
                        {...fieldActions}
                        options={[{value: 'true', key:'Yes'},{value: 'false', key: 'No'}]}
                        onChange={(val)=>{
                            fieldActions.onChange(val, 'active');
                        }}
                    />
                </div> 
                {state.errors.formError && (
                    <div className="my-4">
                        <p className="text-red-400 font-medium ml-2">{state.errors.formError}</p>
                    </div>
                )}
                <div className="flex items-center mt-8 justify-between">
                    <div className="flex">
                        <button 
                            className="py-2 px-4 text-red-500 text-sm rounded-full flex items-center border-red-500 border-2" 
                            onClick={onResetPassword}
                        >
                            Reset Password
                        </button>
                        {
                            authCtx.hasRole(ROLES.ADMIN) && 
                            <button 
                                className= "ml-4 py-2 px-4 text-red-500 text-sm rounded-full flex items-center border-red-500 border-2" 
                                onClick={onDeleteUser}
                            >
                                Delete Account
                            </button>
                        }
                    </div>
                    <div>
                        <Link 
                            to="/useradmin" 
                            className="inline-flex mr-4 py-2 px-4 rounded-full text-blueGray-100 bg-gray-100 font-semibold"
                        >
                            Cancel
                        </Link>
                        <button 
                            className="inline-flex py-2 px-4 text-white text-sm rounded-full flex items-center bg-lightGreen-300" 
                            onClick={() => formActions.submit(() => history.push('/useradmin'))}
                        >
                            Update
                        </button>
                    </div>
                </div>
            </div>
            {
                state.values.showDeleteModal &&
                <DeleteUser 
                    user_id = {userId}
                    showDeleteModal = {state.values.showDeleteModal}
                    hideDeleteModal = {hideDeleteModal}
                />
            }
        </Layout>
    );
};

export default EditUser;